import { Select, Spin, Form } from "antd";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import debounce from "lodash/debounce";
import { searchVendorsPaged, getVendedorByNo } from "../../../../services/vendorService";

const SalesPersonSelect = ({
    name = "Salesperson_Code",
    value = null
}) => {
    const form = Form.useFormInstance();
    const formValue = Form.useWatch(name, form);
    const [options, setOptions] = useState([]);
    const [fetching, setFetching] = useState(false);
    const pageRef = useRef(1);
    const searchRef = useRef("");
    const [hasMore, setHasMore] = useState(true);
    const fetchingRef = useRef(false);

    const prevValueRef = useRef(value);
    const [isManuallyCleared, setIsManuallyCleared] = useState(false);

    useEffect(() => {
        if (formValue) {
            setIsManuallyCleared(false);
        }
    }, [formValue]);

    useEffect(() => {
        if (value !== null && form) {
            const currentValue = form.getFieldValue(name);
            if (
                currentValue === undefined ||
                currentValue === null ||
                currentValue === "" ||
                prevValueRef.current !== value
            ) {
                if (!isManuallyCleared || prevValueRef.current !== value) {
                    form.setFieldValue(name, value);
                    setIsManuallyCleared(false);
                }
            }
        }
        prevValueRef.current = value;
    }, [value, name, form, isManuallyCleared]);

    useEffect(() => {
        const targetValue = formValue !== undefined && formValue !== null ? formValue : value;
        if (targetValue) {
            getVendedorByNo(targetValue).then((vendor) => {
                if (vendor) {
                    setOptions((prev) => {
                        const exists = prev.some((opt) => opt.value === vendor.value);
                        if (exists) return prev;
                        return [
                            {
                                label: `${vendor.value} - ${vendor.label}`,
                                value: vendor.value,
                            },
                            ...prev,
                        ];
                    });
                }
            });
        }
    }, [value, formValue]);

    const loadVendors = useCallback(async (searchValue, pageValue = 1) => {
        if (fetchingRef.current) return;

        fetchingRef.current = true;
        setFetching(true);

        try {
            const { data, total } = await searchVendorsPaged({
                search: searchValue,
                page: pageValue,
                pageSize: 10,
            });

            const newOptions = data.map((v) => ({
                label: `${v.value} - ${v.label}`,
                value: v.value,
            }));

            setOptions((prev) => {
                // si es primera página reset total
                if (pageValue === 1) {
                    return newOptions;
                }

                // si es scroll acumular
                const map = new Map();
                [...prev, ...newOptions].forEach((opt) => {
                    map.set(opt.value, opt);
                });

                return Array.from(map.values());
            });

            setHasMore(pageValue * 10 < total);
        } catch (error) {
            console.error("Error al cargar vendedores:", error);
        } finally {
            fetchingRef.current = false;
            setFetching(false);
        }
    }, []);

    const debouncedSearch = useMemo(
        () =>
            debounce((value) => {
                searchRef.current = value;
                pageRef.current = 1;
                setOptions([]);
                loadVendors(value, 1);
            }, 300),
        [loadVendors]
    );

    const handleSearch = (value) => {
        debouncedSearch(value);
    };

    const handlePopupScroll = (e) => {
        const target = e.target;

        if (
            target.scrollTop + target.offsetHeight >= target.scrollHeight - 80 &&
            !fetchingRef.current &&
            hasMore
        ) {
            pageRef.current += 1;
            loadVendors(searchRef.current, pageRef.current);
        }
    };

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    return (
        <Form.Item name={name} label="Vendedor">
            <Select
                allowClear
                showSearch
                placeholder="Buscar vendedor"
                filterOption={false}
                loading={fetching}
                onSearch={handleSearch}
                onChange={(val) => {
                    if (!val) {
                        setIsManuallyCleared(true);
                        debouncedSearch.cancel?.();
                        searchRef.current = "";
                        pageRef.current = 1;
                        loadVendors("", 1);
                    } else {
                        setIsManuallyCleared(false);
                    }
                }}
                options={options}
                notFoundContent={fetching ? <Spin size="small" /> : null}
                onPopupScroll={handlePopupScroll}
                style={{ width: "100%" }}
                onOpenChange={(open) => {
                    if (!open) {
                        // reseteo opciones al cerrar
                        const targetValue = formValue !== undefined && formValue !== null ? formValue : value;
                        const selectedOption = options.find((opt) => opt.value === targetValue);
                        setOptions(selectedOption ? [selectedOption] : []);
                        pageRef.current = 1;
                        searchRef.current = "";
                        setHasMore(true);
                    }

                    if (open && options.length === 0) {
                        loadVendors("", 1);
                    }
                }}
            />
        </Form.Item>
    );
};

export default SalesPersonSelect;
